/**
 * Client of manager-api's container operations (`docs/api/machine-operations-v1.md`,
 * `restart_container` / `containers_info`): one operation per machine, run by celery-vpn,
 * polled until it settles. Store-level actions fan out over the store's machines and
 * report one aggregated result.
 */

import { toast } from "react-toastify";

import http from "./httpService";
import { getServerByHostname } from "./serversService";

const TERMINAL_STATUSES = new Set(["succeeded", "failed", "expired"]);
const FALLBACK_POLL_MS = 1000;
const MAX_START_ATTEMPTS = 3;
const MAX_POLL_FAILURES = 3;
// 60 s queue deadline + 45 s hard execution limit, with margin.
const OPERATION_DEADLINE_MS = 3 * 60 * 1000;

const ERROR_MESSAGES = {
  machine_busy: "a restart is already running",
  machine_disabled: "the machine is disabled",
  client_timeout: "no final status received",
  upstream_timeout: "the machine did not answer in time",
  upstream_unreachable: "the machine is unreachable",
  upstream_error: "the machine answered an error",
  cloud_not_allowed: "cloud machine not allowed in this environment",
  unknown_target: "unknown machine",
  queue_expired: "the operation waited too long in the queue",
  worker_lost: "the operation was interrupted",
  execution_failed: "the operation failed",
};

/**
 * Turn an operation error code into a readable message.
 *
 * @param {string|null|undefined} code - The `error_code` of an operation, or of a 409 body.
 * @returns {string} The message; the code itself when it is unknown.
 */
export function describeMachineOperationError(code) {
  return ERROR_MESSAGES[code] || code || "unexpected error";
}

function newIdempotencyKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const withJitter = (ms) => ms + Math.floor(Math.random() * ms * 0.2);

function retryAfterMs(error) {
  const seconds = Number(error?.response?.headers?.["retry-after"]);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 5000;
}

const operationsUrl = (hostname) => `/machines/${encodeURIComponent(hostname)}/operations/`;

async function startOperation(hostname, action, params) {
  // One key for every attempt: a retry after a lost response lands on the operation it created.
  const key = newIdempotencyKey();
  for (let attempt = 1; ; attempt += 1) {
    try {
      const { data } = await http.post(
        operationsUrl(hostname),
        { action, params },
        { headers: { "Idempotency-Key": key } }
      );
      return data;
    } catch (error) {
      if (error?.response?.status !== 503 || attempt >= MAX_START_ATTEMPTS) throw error;
      await sleep(retryAfterMs(error));
    }
  }
}

async function pollUntilSettled(hostname, operation) {
  const deadline = Date.now() + OPERATION_DEADLINE_MS;
  let current = operation;
  let failures = 0;
  while (!TERMINAL_STATUSES.has(current?.status)) {
    if (Date.now() > deadline) return { ...current, status: "failed", error_code: "client_timeout" };
    await sleep(withJitter(current?.poll_after_ms || FALLBACK_POLL_MS));
    try {
      const { data } = await http.get(`${operationsUrl(hostname)}${operation.operation_id}/`);
      current = data;
      failures = 0;
    } catch (error) {
      // A 404 means the operation is gone (expired or never ours): polling again cannot help.
      failures += 1;
      if (error?.response?.status === 404 || failures >= MAX_POLL_FAILURES) throw error;
    }
  }
  return current;
}

/**
 * Run one container operation on a machine and wait for it to settle.
 *
 * @param {string} hostname - The machine hostname.
 * @param {"restart_container"|"containers_info"} action - The action.
 * @param {object} params - `{ container }`.
 * @returns {Promise<{ok: boolean, result?: object, error?: string}>} `error` is an error code;
 *   a refused launch (`409 machine_busy`, ...) or a lost operation also resolves, never rejects.
 */
export async function runMachineOperation(hostname, action, params) {
  try {
    const settled = await pollUntilSettled(hostname, await startOperation(hostname, action, params));
    if (settled.status === "succeeded") return { ok: true, result: settled.result };
    return { ok: false, error: settled.error_code || settled.status };
  } catch (error) {
    const data = error?.response?.data;
    return { ok: false, error: data?.error || data?.detail || error?.message };
  }
}

/**
 * List the hostnames of the machines running the store's cameras.
 *
 * @param {string} location - The store location.
 * @returns {Promise<string[]>} The hostnames.
 */
export async function getStoreHostnames(location) {
  const { data } = await http.get(`/stores/${encodeURIComponent(location)}/inference-machines/`);
  return (data || []).filter(Boolean);
}

async function unreachableMachineStatus(hostname) {
  // ServerServices reads every key as a container object, so no value may be null.
  const status = { status: "NOK", uptime: "", image: "", is_cloud: false };
  try {
    const { data: machine } = await getServerByHostname(hostname);
    if (!machine.cloud_identifier) return { ...status, id: machine.id };
    return { ...status, id: machine.id, is_cloud: true, cloud_identifier: machine.cloud_identifier };
  } catch {
    return status;
  }
}

/**
 * Read the container status of every machine of a store.
 *
 * A machine that cannot be read gets a `NOK` placeholder carrying its id, so the per-server
 * links still render for the machines that are down.
 *
 * @param {string} location - The store location.
 * @returns {Promise<{status: Object<string, object>}>} Container status per hostname.
 */
export async function getStoreContainersStatus(location) {
  const hostnames = await getStoreHostnames(location);
  const entries = await Promise.all(
    hostnames.map(async (hostname) => {
      const outcome = await runMachineOperation(hostname, "containers_info", { container: "all" });
      return [hostname, outcome.ok ? outcome.result : await unreachableMachineStatus(hostname)];
    })
  );
  return { status: Object.fromEntries(entries) };
}

/**
 * Restart a container on several machines at once.
 *
 * @param {string[]} hostnames - The machines.
 * @param {string} container - The container name.
 * @returns {Promise<{succeeded: string[], failed: {hostname: string, error: string}[]}>}
 */
export async function restartContainerOnMachines(hostnames, container) {
  const outcomes = await Promise.all(
    hostnames.map((hostname) => runMachineOperation(hostname, "restart_container", { container }))
  );
  const summary = { succeeded: [], failed: [] };
  outcomes.forEach((outcome, index) => {
    if (outcome.ok) summary.succeeded.push(hostnames[index]);
    else summary.failed.push({ hostname: hostnames[index], error: outcome.error });
  });
  return summary;
}

function describeRestartSummary({ succeeded, failed }, container) {
  const total = succeeded.length + failed.length;
  if (total === 0) return { type: toast.TYPE.ERROR, render: `No server to restart ${container} on` };
  const headline = `${container}: ${succeeded.length}/${total} restarted`;
  if (failed.length === 0) return { type: toast.TYPE.SUCCESS, render: headline };
  const failures = failed
    .map(({ hostname, error }) => `${hostname}: ${describeMachineOperationError(error)}`)
    .join("; ");
  return { type: toast.TYPE.ERROR, render: `${headline}. ${failures}` };
}

/**
 * Restart a container on several machines behind one toast: in progress, then the global result
 * (e.g. `simone-inference: 2/3 restarted. nuc-1: the machine is unreachable`).
 *
 * @param {string[]} hostnames - The machines.
 * @param {string} container - The container name.
 * @returns {Promise<{succeeded: string[], failed: {hostname: string, error: string}[]}>}
 */
export async function restartWithToast(hostnames, container) {
  const toastId = toast.info(`Restarting ${container} on ${hostnames.length} server(s)...`, { autoClose: false });
  const summary = await restartContainerOnMachines(hostnames, container);
  toast.update(toastId, { ...describeRestartSummary(summary, container), autoClose: 8000 });
  return summary;
}
